/**
 * @file memberActions.js
 * @location src/store/reducers/memberActions.js
 * @role 인원 관리(members CRUD) + 초대 (책임 분리 razor) 액션 핸들러
 *
 *  ADD_MEMBER         { member }              id 미지정 시 newId('member'). member.pending 가 있으면 초대중 자리만
 *  EDIT_MEMBER        { id, changes }         부분 필드 패치
 *  REISSUE_INVITE     { id }                  pending 멤버의 코드·invitedAt 재발급 (만료 연장)
 *  DELETE_MEMBER      { id }                  본인(state.uid) 삭제는 차단
 *    실제 OAuth/계정 생성은 백엔드 필요 → 화면상 프로필 CRUD 만 동작.
 *    초대 코드 수락(pending → 정식 멤버 전환)은 Supabase 경로(JoinScreen → acceptInvite RPC)가 소유 — 리듀서엔 없음.
 *    초대 TTL: 7일 (inviteCode.js INVITE_TTL_DAYS). 만료 코드는 REISSUE_INVITE 로 연장.
 *    관련 task(aee/aor)는 그대로 두므로 외래키 무결성은 호출 측 confirm 책임.
 */
import { genInviteCode } from '../../utils/inviteCode.js';
import { newId } from '../../utils/ids.js';

function buildMemberRecord(m, allMembers) {
  const id = m.id ?? newId('member');
  const base = {
    id, name: m.name ?? '',
    email:     m.email     ?? '',
    mainCases: Array.isArray(m.mainCases) ? m.mainCases : [],
  };
  if (m.isManager === true) base.isManager = true;
  if (m.pending) {
    const existing = allMembers
      .map(x => x.pending?.code)
      .filter(Boolean);
    base.pending = {
      code:      m.pending.code ?? genInviteCode(existing),
      invitedAt: m.pending.invitedAt ?? new Date().toISOString(),
    };
  }
  return base;
}

export const memberActions = {
  // pending 자리는 name 미요구 (책임 분리 razor: 이름 도 본인이 입력).
  // 정식 멤버 (pending 없음) 추가는 name 필수 — 데이터 마이그레이션 등 예외 경로용
  ADD_MEMBER: (state, action) => {
    const m = action.member ?? {};
    if (!m.pending && !m.name) return state;
    const next = buildMemberRecord(m, state.members);
    return {
      ...state,
      members: [...state.members, next],
    };
  },

  EDIT_MEMBER: (state, action) => ({
    ...state,
    members: state.members.map(m =>
      m.id !== action.id ? m : { ...m, ...action.changes }
    ),
  }),

  // 본인 프로필(이름) 자가 수정 — 대상은 항상 로그인 본인(state.uid).
  UPDATE_PROFILE: (state, action) => {
    const name = (action.name ?? '').trim();
    if (!name) return state;
    return {
      ...state,
      members: state.members.map(m => (m.id !== state.uid ? m : { ...m, name })),
    };
  },

  // 초대 코드 재발급 (만료 연장 + 새 코드). pending 멤버만 대상.
  REISSUE_INVITE: (state, action) => {
    const target = state.members.find(m => m.id === action.id);
    if (!target?.pending) return state;
    const existing = state.members.map(x => x.pending?.code).filter(Boolean);
    const newCode = genInviteCode(existing.filter(c => c !== target.pending.code));
    return {
      ...state,
      members: state.members.map(m => m.id === target.id
        ? { ...m, pending: { code: newCode, invitedAt: new Date().toISOString() } }
        : m),
    };
  },

  DELETE_MEMBER: (state, action) => {
    if (action.id === state.uid) return state; // 본인 삭제 방지
    // task.aee·aor 는 razor 정합 (「업무 데이터는 남김」) 로 보존.
    return {
      ...state,
      members: state.members.filter(m => m.id !== action.id),
    };
  },
};
